import { Link } from "react-router-dom";
import { categories, type Article } from "@/mocks/content";

function categoryName(slug: string) {
  return categories.find((c) => c.slug === slug)?.name ?? "话题";
}

interface Props {
  article: Article;
  variant?: "default" | "horizontal" | "compact";
  index?: number;
}

export default function ArticleCard({ article, variant = "default", index }: Props) {
  const to = `/article/${article.id}`;
  const catName = categoryName(article.categorySlug);

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex items-center gap-4 py-4 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        {typeof index === "number" && (
          <span
            className={`w-8 h-8 flex-shrink-0 flex items-center justify-center font-heading font-bold text-base rounded-md ${
              index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
            }`}
          >
            {index + 1}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="text-secondary-700">{catName}</span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-fire-line text-sm" />
              </span>
              {article.views.toLocaleString("zh-CN")}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={to}
        className="group flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-md overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={`${article.title} - ${catName}内容封面`}
            title={`${article.title} 看料91`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 mb-2">
            {catName}
          </span>
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2 hidden sm:block">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line text-sm" />
              </span>
              {article.views.toLocaleString("zh-CN")}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group">
      <Link to={to} className="block cursor-pointer">
        <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-100 mb-4">
          <img
            src={article.cover}
            alt={`${article.title} - ${catName}内容封面`}
            title={`${article.title} 看料91`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
            {catName}
          </span>
          {article.hot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 flex items-center gap-0.5">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-fill text-xs" />
              </span>
              热料
            </span>
          )}
        </div>
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-eye-line text-sm" />
            </span>
            {article.views.toLocaleString("zh-CN")}
          </span>
        </div>
      </Link>
    </article>
  );
}